import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PaymentType, Transaction, TransactionStatus } from "../../../../models/Transactions";
import Loader from "../../components/Loader";
import { useInView } from "react-intersection-observer";
import { useTheme } from "../../../../hooks/useTheme";
import { useWallet } from "../../../../hooks/useWallet";
import { useNavigate } from "react-router-dom";
import { dateStringToLocaleDateWithoutYear, formatAmount, getFlagEmoji, localeFormatNumber } from "../../../../utils/Utils";
import { Button, ButtonSize } from "../../../../components/Button";
import { ButtonVariant } from "../../../../config/colors";
import { OPAAvatar } from "../../../../components/OPAAvatar";
import { BulkTypes } from "../../../../models/BulkPayment";

interface WalletTransactionsProps {

}   

type FilterType = 'all' | 'sent' | 'received';

const WalletTransactions: React.FC<WalletTransactionsProps> = () => {
  const { transactions, isLoading, error, hasMore, loadTransactions } = useWallet();
  const { ref, inView } = useInView();
  const isMobile = useTheme();
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const navigate = useNavigate();

  // Filter transactions before grouping
  const filteredTransactions = useMemo(() => {
    return transactions.filter(transaction => {
        if (activeFilter === 'all') return true;
        const isSent = transaction.amount < 0;
        return activeFilter === 'sent' ? isSent : !isSent;
    });
  }, [transactions, activeFilter]);

  // Group transactions by date
  const groupedTransactions = useMemo(() => {
    const groups: { [key: string]: Transaction[] } = {};
    
    filteredTransactions.forEach(transaction => {
      const date = new Date(transaction.time);
      // Format the date as "Month Year" (e.g., "March 2024")
      const dateKey = date.toLocaleString('en-US', { month: 'long', year: 'numeric' });

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(transaction);
    });

    // Sort the transactions within each group by date (newest first)
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
    });

    return groups;
  }, [filteredTransactions]);

  useEffect(() => {
    if (inView) {
      loadTransactions();
    }
  }, [inView, loadTransactions]);

  const showTransactionDetailLeft = useCallback((transaction: Transaction) => {
    let isSend = transaction.amount < 0;

    if(transaction.type === PaymentType.bulk)
    {
        const nbToSend = transaction.bulk?.nbToSend ?? 0;

        return <div className="flex flex-row items-center gap-2">
            {
                transaction.bulk?.type === BulkTypes.city && (
                    <div className="shrink-0">
                        <OPAAvatar
                            size={33}
                            borderWidth={0}
                            borderColor="var(--brand-bg-2)"
                        >
                            <div className="flex items-center justify-center w-full h-full bg-brand-bg-4">
                                {
                                    transaction.bulk?.geoZap?.addressType === 'country' ? (
                                        transaction.bulk.geoZap.countryImgUrl ? (
                                            <img src={transaction.bulk.geoZap.countryImgUrl} className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-2xl">{getFlagEmoji(transaction.bulk?.geoZap?.countryCode ?? '')}</span>
                                        )
                                    ) : (
                                        <span className="material-symbols-outlined">location_city</span>
                                    )
                                }
                            </div>
                        </OPAAvatar>
                    </div>
                )
            }
            {
                transaction.bulk?.type === BulkTypes.distanceFromMyself && (
                    <div className="shrink-0">
                        <OPAAvatar
                            size={33}
                            borderWidth={0}
                            borderColor="var(--brand-bg-2)"
                        >
                            <div className="flex items-center justify-center w-full h-full bg-brand-bg-4">
                                <span className="material-symbols-outlined">location_on</span>
                            </div>
                        </OPAAvatar>
                    </div>
                )
            }
            <div>
                <div className="text-sm font-semibold">
                    {transaction.bulk?.name}
                </div>
                <div className="text-xs text-brand-color-2">
                    {formatAmount(nbToSend)} recipient{nbToSend <= 1 ? '' : 's'}
                </div>
            </div>
        </div>
    }

    if(!isSend && transaction.sender && transaction.sender.internalUser)
    {
        return <div className="flex flex-row items-center gap-2">
            <div className="shrink-0">
                <OPAAvatar
                    imageUrl={transaction.sender?.internalUser?.image}
                    size={33}
                    borderWidth={0}
                    borderColor="var(--brand-bg-2)"
                />
            </div>
            <div>
                <div className="text-xs font-semibold text-brand-color-2">
                    {dateStringToLocaleDateWithoutYear(transaction.time)}
                </div>
                <div className="text-sm font-semibold">
                    {transaction.sender.internalUser.name}
                </div>
            </div>
        </div>
    }
    else if(isSend && transaction.recipient && transaction.recipient.internalUser)
    {
        return <div className="flex flex-row items-center gap-2">
            <div className="shrink-0">
                <OPAAvatar
                    imageUrl={transaction.recipient?.internalUser?.image}
                    size={33}
                    borderWidth={0}
                    borderColor="var(--brand-bg-2)"
                />
            </div>
            <div>
                <div className="text-xs font-semibold text-brand-color-2">
                    {dateStringToLocaleDateWithoutYear(transaction.time)}
                </div>
                <div className="text-sm font-semibold">
                    {transaction.recipient.internalUser.name}
                </div>
            </div>
        </div>
    }
    else
        return <>
            <div className="text-xs font-semibold text-brand-color-2">
                {dateStringToLocaleDateWithoutYear(transaction.time)}
            </div>
            <div className={`text-sm font-semibold ${isSend ? '' : 'text-brand-green'}`}>{isSend ? 'Sent' : 'Received'}</div>
        </>
  }, []);

  if(error) {
    return <div className={`flex flex-col items-center ${isMobile ? 'justify-start' : 'justify-center'} px-4 py-4 h-full`}>
        <p className="text-center text-base opacity-50">
            {error}
        </p>
    </div>;
  }

  return (
    <div className="container mx-auto">
        <div className="text-xl font-bold text-center">
            Transactions
        </div>

        <div className="mt-4">
            <div className="flex space-x-2 overflow-x-auto scrollbar-hide">
                {(['all', 'sent', 'received'] as FilterType[]).map((filter) => {
                    const isSelected = activeFilter === filter;
                    let buttonVariant = isSelected ? ButtonVariant.inverted : ButtonVariant.unselected;

                    if(filter === 'received' && isSelected)
                        buttonVariant = ButtonVariant.green;

                    return <Button 
                        key={filter} 
                        variant={buttonVariant} 
                        size={ButtonSize.medium}
                        onClick={() => {
                            setActiveFilter(filter);
                        }}
                        className="flex-1"
                    >
                        {filter.charAt(0).toUpperCase() + filter.slice(1)} 
                    </Button>
                })}
            </div>
        </div>

        <div className="mt-4 flex-1">
            {Object.entries(groupedTransactions).map(([date, dateTransactions]) => (
                <div key={date} className="mb-4">
                    <div className="flex flex-row items-center gap-2">
                        <div className="font-semibold">
                            {date}
                        </div>
                        
                    </div>
                    <div className="flex flex-col mt-2">
                        {dateTransactions.map((transaction) => {
                            const isSend = transaction.amount < 0;

                            return <div 
                                    key={transaction.paymentHash} 
                                    onClick={() => navigate(`/home/wallet/tx/${transaction.paymentHash}`)}
                                    className="cursor-pointer border-b border-brand-border-color"
                                >
                                <div className="rounded-md hover:bg-brand-bg-3 py-2 px-4">
                                    <div className="flex flex-row justify-between items-center gap-4">
                                        <div className="flex flex-row items-center gap-2">
                                            {
                                                !transaction.read && (
                                                    <div className="w-2 h-2 bg-brand-red rounded-full" />
                                                )
                                            }
                                            {
                                                transaction.status === TransactionStatus.PENDING && (
                                                    <Loader xsmall />
                                                )
                                            }
                                            <div className="flex flex-col">
                                                {showTransactionDetailLeft(transaction)}
                                            </div>
                                        </div>
                                        <div className={`shrink-0 text-xl font-semibold ${isSend ? '' : 'text-brand-green'}`}>
                                            {
                                                transaction.amount !== 0 && <div>{isSend ? '' : '+'}{transaction.amount.toLocaleString(localeFormatNumber)} SATS</div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>;
                        })}
                    </div>
                </div>
            ))}

            {filteredTransactions.length === 0 && !isLoading && (
                <div className="flex flex-col items-center justify-center h-full mt-8">
                    <div className="text-center text-base opacity-50">
                        No {activeFilter !== 'all' ? activeFilter : ''} transactions found
                    </div>
                </div>
            )}
        </div>

        <div
            className="h-20 flex items-center justify-center"
        >
            {isLoading && <div className="flex justify-center items-center py-4"><Loader small /></div>}
            {hasMore && <div ref={ref} className="h-5" />}
        </div>
    </div>
  );
}

export default WalletTransactions;